import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { apiPut, prettifyApiError } from "@/shared/api";
import {
  type CampoIndirizzo,
  campiOltreLimite,
  ETICHETTE_CAMPO,
  LIMITI_POSTE,
  lunghezza,
  type ValoriIndirizzo,
} from "../../limitiPoste";
import type { Order } from "../../types";

/** Il nome che mapOrder mette quando il canale non ne manda uno: non è un
 *  destinatario, non va proposto come valore da salvare. */
const NOME_ASSENTE = "Cliente non disponibile";

export function valoriIndirizzo(order: Order): ValoriIndirizzo {
  const c = order.customer;
  return {
    name: c.name === NOME_ASSENTE ? "" : c.name,
    address: c.address ?? "",
    postcode: c.postcode ?? "",
    city: c.cityName ?? "",
    province: c.province ?? "",
    phone: c.phone ?? "",
  };
}

type Props = { order: Order; onClose: () => void; onSaved?: () => void };

/** Correzione a mano dell'indirizzo di spedizione (PUT .../shipping-address).
 *  Resta anche dopo il sync dal canale. Vale per le LDV fatte da OM (SDA e
 *  Poste): quelle chieste a Poleepo usano l'indirizzo che ha Poleepo. */
export function IndirizzoDialog({ order, onClose, onSaved }: Props) {
  const [valori, setValori] = useState<ValoriIndirizzo>(() => valoriIndirizzo(order));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const oltre = campiOltreLimite(valori);
  const canSubmit = valori.name.trim() !== "" && valori.address.trim() !== "" && !busy;

  async function handleSubmit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await apiPut(`/api/orders/${encodeURIComponent(order.id)}/shipping-address`, valori);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  const campo = (nome: CampoIndirizzo, span: string, extra?: { autoFocus?: boolean }) => {
    const n = lunghezza(nome, valori[nome]);
    const max = LIMITI_POSTE[nome];
    const lungo = n > max;
    const idCampo = `indirizzo-${nome}`;
    return (
      <div className={`flex flex-col gap-1 ${span}`}>
        <div className="flex items-baseline justify-between gap-2">
          <label htmlFor={idCampo} className="text-[12px] font-semibold text-ink">
            {ETICHETTE_CAMPO[nome]}
          </label>
          <span
            id={`${idCampo}-conta`}
            className={`text-[11px] tabular-nums text-ink ${lungo ? "font-bold text-red" : ""}`}
          >
            {n}/{max}
          </span>
        </div>
        <input
          id={idCampo}
          value={valori[nome]}
          disabled={busy}
          autoFocus={extra?.autoFocus}
          aria-invalid={lungo || undefined}
          aria-describedby={`${idCampo}-conta`}
          onChange={(e) => setValori({ ...valori, [nome]: e.target.value })}
          className={`h-[40px] rounded-md border bg-card px-3 text-[13px] text-ink outline-none disabled:opacity-50 ${
            lungo ? "border-red bg-red-soft focus:border-red" : "border-line focus:border-ink"
          }`}
        />
      </div>
    );
  };

  // Portale sul body: dentro la colonna della pagina il popup restava sotto
  // i tasti della griglia Spedizione, che si vedevano attraverso (28/09).
  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Modifica indirizzo di spedizione"
      data-no-open
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        className="w-full max-w-lg rounded-lg border border-line bg-card p-5 shadow-popover"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <h3 className="font-serif text-[16px] font-medium text-ink">Indirizzo di spedizione</h3>
        <p className="mt-1 text-[12px] text-ink">
          Ordine {order.id}. La correzione resta anche dopo la sincronizzazione dal marketplace. Il
          contatore mostra i caratteri che Poste Delivery accetta.
        </p>

        <div className="mt-4 grid grid-cols-6 gap-x-3 gap-y-3">
          {campo("name", "col-span-6", { autoFocus: true })}
          {campo("address", "col-span-6")}
          {campo("postcode", "col-span-2")}
          {campo("city", "col-span-3")}
          {campo("province", "col-span-1")}
          {campo("phone", "col-span-6")}
        </div>

        {oltre.length > 0 && (
          <p className="mt-3 rounded-md border border-amber bg-amber-soft px-3 py-2 text-[12px] text-ink">
            <span className="font-semibold">Troppo lungo per Poste Delivery:</span>{" "}
            {oltre
              .map((s) => `${ETICHETTE_CAMPO[s.campo].toLowerCase()} ${s.lunghezza}/${s.max}`)
              .join(" · ")}
            . Con SDA passa; con Poste la LDV viene rifiutata.
          </p>
        )}

        {error && (
          <p role="alert" className="mt-3 text-[12px] font-semibold text-red">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink hover:border-ink-4 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-md border border-teal bg-teal px-3 py-2 text-[12px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Salvo…" : "Salva indirizzo"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
